<template>
  <div class="about-page">
    <!-- ===== PAGE HERO ===== -->
    <section class="page-hero">
      <div class="hero-aurora" aria-hidden="true">
        <div class="ha-orb ha-orb-1"></div>
        <div class="ha-orb ha-orb-2"></div>
      </div>
      <div class="dot-grid" aria-hidden="true"></div>

      <div class="container ph-grid">
        <div class="ph-text">
          <div class="section-tag">About Me</div>
          <h1 class="ph-title">
            Creative technologist
            <span class="gradient-text">building the future</span>
            of fashion AI.
          </h1>
          <p class="ph-sub">
            From dreaming of Marvel VFX credits to building AI systems used by thousands daily —
            where creativity meets engineering.
          </p>
        </div>

        <div class="ph-profile">
          <div class="about-ring">
            <div class="about-inner">
              <img src="/images/IMG_20250824_234147.jpg" alt="Vidya Siva" />
            </div>
          </div>
          <div class="avail-badge">
            <span class="avail-dot"></span>
            Available for new projects
          </div>
        </div>
      </div>
    </section>

    <!-- ===== JOURNEY ===== -->
    <section class="journey-section">
      <div class="container">
        <div class="sec-header reveal">
          <div class="section-tag">My Story</div>
          <h2 class="sec-title">The Journey</h2>
        </div>

        <div class="journey-grid">
          <div class="journey-card reveal reveal-delay-1">
            <div class="jc-year">2016</div>
            <div class="jc-body">
              <div class="jc-icon">🎬</div>
              <h3>Creative Beginnings</h3>
              <p>Pursued B.Sc. Visual Communication at SRM University, driven by a dream of working on Marvel VFX. Built a strong foundation in design and visual storytelling.</p>
            </div>
          </div>

          <div class="journey-card reveal reveal-delay-2">
            <div class="jc-year">2018</div>
            <div class="jc-body">
              <div class="jc-icon">🎨</div>
              <h3>First Steps into Tech</h3>
              <p>Joined TRI3D as a 3D Artist. Working with Blender and Python scripting sparked a decisive transition from creative arts into software engineering.</p>
            </div>
          </div>

          <div class="journey-card reveal reveal-delay-3">
            <div class="jc-year">2019</div>
            <div class="jc-body">
              <div class="jc-icon">⚡</div>
              <h3>Engineering Transformation</h3>
              <p>Transitioned to Software Engineering at AlphaBake. Picked up Python, Django, Vue.js, and cloud platforms while contributing to production systems from day one.</p>
            </div>
          </div>

          <div class="journey-card reveal reveal-delay-4">
            <div class="jc-year">Now</div>
            <div class="jc-body">
              <div class="jc-icon">🚀</div>
              <h3>AI at Scale</h3>
              <p>Building AI-powered fashion tech at AlphaBake — virtual try-on systems, ComfyUI generative pipelines, and 3D visualization tools used by thousands daily.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TECH STACK ===== -->
    <section class="tech-section">
      <div class="container">
        <div class="sec-header reveal">
          <div class="section-tag">Skills</div>
          <h2 class="sec-title">Technologies & Tools</h2>
        </div>

        <div class="tech-board reveal">
          <div class="tech-cat">
            <h4><span class="cat-dot blue"></span>Programming & Frameworks</h4>
            <div class="pills">
              <span class="pill">Python</span>
              <span class="pill">Django</span>
              <span class="pill">JavaScript</span>
              <span class="pill">TypeScript</span>
              <span class="pill">Vue.js</span>
              <span class="pill">Node.js</span>
              <span class="pill">Express.js</span>
            </div>
          </div>

          <div class="tech-cat">
            <h4><span class="cat-dot purple"></span>AI & Generative</h4>
            <div class="pills">
              <span class="pill pill-purple">ComfyUI</span>
              <span class="pill pill-purple">LoRA Training</span>
              <span class="pill pill-purple">Model Fine-tuning</span>
              <span class="pill pill-purple">Generative AI</span>
              <span class="pill pill-purple">OpenCV</span>
            </div>
          </div>

          <div class="tech-cat">
            <h4><span class="cat-dot gold"></span>Cloud & DevOps</h4>
            <div class="pills">
              <span class="pill pill-gold">AWS</span>
              <span class="pill pill-gold">GCP</span>
              <span class="pill pill-gold">Azure</span>
              <span class="pill pill-gold">Docker</span>
              <span class="pill pill-gold">Firebase</span>
              <span class="pill pill-gold">Cloud Automation</span>
            </div>
          </div>

          <div class="tech-cat">
            <h4><span class="cat-dot green"></span>Creative & Design</h4>
            <div class="pills">
              <span class="pill pill-green">Blender</span>
              <span class="pill pill-green">GIMP</span>
              <span class="pill pill-green">Photoshop</span>
              <span class="pill pill-green">Adobe Scripting</span>
              <span class="pill pill-green">UI/UX</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TIMELINE ===== -->
    <section class="tl-section">
      <div class="container">
        <div class="sec-header reveal">
          <div class="section-tag">Experience</div>
          <h2 class="sec-title">Work & Education</h2>
        </div>

        <div class="timeline">
          <div class="tl-line" aria-hidden="true"></div>

          <div class="tl-item reveal">
            <div class="tl-marker">
              <div class="tl-dot blue-dot"></div>
            </div>
            <div class="tl-card">
              <div class="tl-head">
                <div>
                  <div class="tl-role">Software Development Engineer</div>
                  <div class="tl-company">AlphaBake (formerly TRI3D)</div>
                </div>
                <div class="tl-period">Jun 2019 — Present</div>
              </div>
              <p class="tl-desc">
                Working across the full product lifecycle — design to deployment. Building virtual try-on systems,
                generative AI pipelines with ComfyUI, Shopify integrations, and GPU cloud automation tools.
              </p>
            </div>
          </div>

          <div class="tl-item reveal reveal-delay-1">
            <div class="tl-marker">
              <div class="tl-dot blue-dot"></div>
            </div>
            <div class="tl-card">
              <div class="tl-head">
                <div>
                  <div class="tl-role">3D Artist</div>
                  <div class="tl-company">TRI3D</div>
                </div>
                <div class="tl-period">Dec 2018 — Jun 2019</div>
              </div>
              <p class="tl-desc">
                Created 3D garment visuals using Blender, GIMP, and Photoshop. Developed Sviewer —
                a desktop fabric visualization tool that later evolved into the web-based Fab3D platform.
              </p>
            </div>
          </div>

          <div class="tl-item reveal reveal-delay-2">
            <div class="tl-marker">
              <div class="tl-dot gold-dot"></div>
            </div>
            <div class="tl-card">
              <div class="tl-head">
                <div>
                  <div class="tl-role">B.Sc. Visual Communication</div>
                  <div class="tl-company">SRM University, Chennai</div>
                </div>
                <div class="tl-period">2016 — 2019</div>
              </div>
              <p class="tl-desc">
                Specialized in visual design, digital media, and communication. This creative foundation
                now informs my approach to building intuitive AI-driven visual tools.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CERTIFICATIONS ===== -->
    <section class="cert-section">
      <div class="container">
        <div class="sec-header reveal">
          <div class="section-tag">Certifications</div>
          <h2 class="sec-title">Always Learning</h2>
        </div>

        <div class="cert-grid reveal">
          <div class="cert-card">
            <div class="cert-emoji">🧠</div>
            <div class="cert-info">
              <h4>Deep Learning</h4>
              <p>IIT Madras</p>
              <span>Jan – May 2022</span>
            </div>
          </div>
          <div class="cert-card">
            <div class="cert-emoji">🐍</div>
            <div class="cert-info">
              <h4>Python for Data Science</h4>
              <p>IIT Madras</p>
              <span>Jan – Mar 2022</span>
            </div>
          </div>
          <div class="cert-card">
            <div class="cert-emoji">🎬</div>
            <div class="cert-info">
              <h4>VFX</h4>
              <p>Davinci Media College, Chennai</p>
              <span>2017 – 2018</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== LOGO STORY CTA ===== -->
    <section class="logo-cta-section">
      <div class="container">
        <div class="logo-cta-card reveal">
          <div class="logo-cta-mark">
            <svg width="52" height="40" viewBox="-12 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="lcvg1" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"   stop-color="#5b8fff"/>
                  <stop offset="100%" stop-color="#a78bfa"/>
                </linearGradient>
                <linearGradient id="lcvg2" x1="4" y1="0" x2="48" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%"   stop-color="#a0c8ff"/>
                  <stop offset="100%" stop-color="#d4b8ff"/>
                </linearGradient>
              </defs>
              <circle cx="4"  cy="4" r="3" fill="#5b8fff"/>
              <circle cx="48" cy="4" r="3" fill="#a78bfa"/>
              <line x1="2.3"  y1="5"  x2="24.3" y2="43" stroke="url(#lcvg2)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="5.7"  y1="3"  x2="27.7" y2="41" stroke="url(#lcvg1)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="46.3" y1="3"  x2="24.3" y2="41" stroke="url(#lcvg1)" stroke-width="2.2" stroke-linecap="round"/>
              <line x1="49.7" y1="5"  x2="27.7" y2="43" stroke="url(#lcvg2)" stroke-width="2.2" stroke-linecap="round"/>
              <text x="-9" y="24" font-family="monospace" font-size="9" font-weight="700" fill="#5b8fff" opacity="0.9">&lt;/&gt;</text>
              <circle cx="41.8" cy="14.6" r="8" fill="none" stroke="#a78bfa" stroke-width="1.1" stroke-dasharray="2.4 3" opacity="0.6"/>
              <ellipse cx="41.8" cy="14.6" rx="8" ry="3.2" fill="none" stroke="#a78bfa" stroke-width="0.7" stroke-dasharray="1.5 2.5" opacity="0.25"/>
              <g>
                <path d="M49.8,18.2 L52.8,11.2 L49.8,13 L46.8,11.2 Z" fill="#e8b84b"/>
                <animateTransform attributeName="transform" type="rotate" from="0 41.8 14.6" to="360 41.8 14.6" dur="4.5s" repeatCount="indefinite"/>
              </g>
            </svg>
          </div>
          <div class="logo-cta-text">
            <h3>There's a story behind the logo</h3>
            <p>Every element — the double arms, the orbit, the plane — was designed with intent. See what it all means.</p>
          </div>
          <RouterLink to="/logo" class="logo-cta-btn">
            The Story Behind My Logo
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </RouterLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

let observer: IntersectionObserver | null = null

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible') }),
    { threshold: 0.1 }
  )
  document.querySelectorAll('.reveal').forEach(el => observer!.observe(el))
})

onUnmounted(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.about-page {
  padding-top: 80px;
}

/* ===== PAGE HERO ===== */
.page-hero {
  position: relative;
  padding: var(--space-20) 0 var(--space-16);
  overflow: hidden;
}

.hero-aurora {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.ha-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.1;
}

.ha-orb-1 {
  width: 600px;
  height: 600px;
  background: var(--purple);
  top: -200px;
  right: -100px;
  animation: orb-move 12s ease-in-out infinite;
}

.ha-orb-2 {
  width: 400px;
  height: 400px;
  background: var(--gold);
  bottom: -100px;
  left: -50px;
  opacity: 0.07;
  animation: orb-move2 10s ease-in-out infinite;
}

@keyframes orb-move {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-20px, 20px); }
}
@keyframes orb-move2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(15px, -15px); }
}

.dot-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
}

[data-theme="light"] .dot-grid {
  background-image: radial-gradient(circle, rgba(0, 0, 0, 0.055) 1px, transparent 1px);
}

.ph-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-16);
  align-items: center;
  position: relative;
  z-index: 1;
}

.ph-text {
  max-width: 640px;
}

.ph-title {
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.15;
  margin-bottom: var(--space-6);
}

.ph-sub {
  font-size: 1rem;
  color: var(--text-muted);
  line-height: 1.72;
  max-width: 520px;
}

/* Profile */
.ph-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
}

.about-ring {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--purple), var(--gold));
  padding: 3px;
}

.about-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: var(--surface);
}

.about-inner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 20%;
  transform: scale(1.15);
  transform-origin: center 25%;
}

.avail-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  font-family: var(--font-display);
  font-weight: 500;
  color: #22c55e;
}

.avail-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  animation: live-pulse 2.2s infinite;
  flex-shrink: 0;
}

@keyframes live-pulse {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  60% { opacity: 0.9; box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
}

/* ===== SHARED SECTION HEADER ===== */
.sec-header {
  text-align: center;
  margin-bottom: var(--space-12);
}

.sec-title {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  letter-spacing: -0.02em;
}

/* ===== JOURNEY ===== */
.journey-section {
  padding: var(--space-24) 0;
}

.journey-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--space-6);
}

.journey-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: all var(--transition-normal);
}

.journey-card:hover {
  border-color: var(--border-light);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.jc-year {
  padding: var(--space-3) var(--space-5);
  background: var(--primary-dim);
  border-bottom: 1px solid rgba(91, 143, 255, 0.15);
  font-family: var(--font-display);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--primary);
  letter-spacing: 0.5px;
}

.jc-body {
  padding: var(--space-6);
}

.jc-icon {
  font-size: 1.8rem;
  margin-bottom: var(--space-4);
}

.jc-body h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: var(--space-3);
}

.jc-body p {
  font-size: 0.88rem;
  line-height: 1.65;
}

/* ===== TECH STACK ===== */
.tech-section {
  padding: var(--space-24) 0;
  background: var(--surface);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
}

.tech-board {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--space-8);
}

.tech-cat h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
}

.cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cat-dot.blue   { background: var(--primary); }
.cat-dot.purple { background: var(--purple); }
.cat-dot.gold   { background: var(--gold); }
.cat-dot.green  { background: var(--green); }

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.pill {
  display: inline-block;
  padding: 5px 12px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  font-size: 0.8rem;
  font-family: var(--font-display);
  font-weight: 500;
  color: var(--text-muted);
  transition: all var(--transition-fast);
  cursor: default;
}

.pill:hover,
.pill-purple:hover,
.pill-gold:hover,
.pill-green:hover {
  transform: translateY(-2px);
}

.pill:hover { background: var(--primary-dim); color: var(--primary); border-color: rgba(91,143,255,0.3); }
.pill-purple { background: var(--surface-2); border-color: var(--border); color: var(--text-muted); }
.pill-purple:hover { background: var(--purple-dim); color: var(--purple); border-color: rgba(167,139,250,0.3); }
.pill-gold { background: var(--surface-2); border-color: var(--border); color: var(--text-muted); }
.pill-gold:hover { background: var(--gold-dim); color: var(--gold); border-color: rgba(232,184,75,0.3); }
.pill-green { background: var(--surface-2); border-color: var(--border); color: var(--text-muted); }
.pill-green:hover { background: var(--green-dim); color: var(--green); border-color: rgba(34,197,94,0.3); }

/* ===== TIMELINE ===== */
.tl-section {
  padding: var(--space-24) 0;
}

.timeline {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
}

.tl-line {
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(to bottom, var(--primary), var(--purple), transparent);
  opacity: 0.3;
}

.tl-item {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: var(--space-5);
  margin-bottom: var(--space-8);
  position: relative;
}

.tl-marker {
  display: flex;
  justify-content: center;
  padding-top: 20px;
}

.tl-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  position: relative;
}

.tl-dot.blue-dot {
  background: var(--primary);
  box-shadow: 0 0 0 3px var(--bg), 0 0 0 5px rgba(91, 143, 255, 0.3);
}

.tl-dot.gold-dot {
  background: var(--gold);
  box-shadow: 0 0 0 3px var(--bg), 0 0 0 5px rgba(232, 184, 75, 0.3);
}

.tl-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  transition: all var(--transition-normal);
}

.tl-card:hover {
  border-color: var(--border-light);
  box-shadow: var(--shadow-md);
}

.tl-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  flex-wrap: wrap;
}

.tl-role {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 4px;
}

.tl-company {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary);
}

.tl-period {
  font-size: 0.78rem;
  font-weight: 500;
  color: var(--text-muted);
  white-space: nowrap;
  padding: 4px 12px;
  background: var(--surface-2);
  border-radius: var(--radius-full);
  border: 1px solid var(--border);
  height: fit-content;
  flex-shrink: 0;
}

.tl-desc {
  font-size: 0.88rem;
  line-height: 1.65;
}

/* ===== CERTIFICATIONS ===== */
.cert-section {
  padding: var(--space-24) 0 var(--space-16);
}

.cert-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--space-6);
  max-width: 820px;
  margin: 0 auto;
}

.cert-card {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-6);
  transition: all var(--transition-normal);
}

.cert-card:hover {
  border-color: var(--border-light);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.cert-emoji {
  font-size: 2rem;
  flex-shrink: 0;
}

.cert-info h4 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.cert-info p {
  font-size: 0.8rem;
  color: var(--primary);
  font-weight: 500;
  margin-bottom: 2px;
}

.cert-info span {
  font-size: 0.76rem;
  color: var(--text-muted);
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .about-page { padding-top: 70px; }

  .ph-grid {
    grid-template-columns: 1fr;
    text-align: center;
    gap: var(--space-10);
  }

  .ph-profile { margin: 0 auto; }

  .ph-sub { max-width: 100%; margin: 0 auto; }

  .about-ring {
    width: 160px;
    height: 160px;
  }

  .journey-grid { grid-template-columns: 1fr; }

  .tech-board { grid-template-columns: 1fr; }

  .tl-line { left: 16px; }

  .tl-item { grid-template-columns: 40px 1fr; }

  .tl-head { flex-direction: column; gap: var(--space-2); }

  .cert-grid { grid-template-columns: 1fr; }
}

/* ===== LOGO CTA ===== */
.logo-cta-section {
  padding: var(--space-8) 0 var(--space-24);
}

.logo-cta-card {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  background: linear-gradient(135deg, rgba(91, 143, 255, 0.06), rgba(167, 139, 250, 0.06));
  border: 1px solid rgba(91, 143, 255, 0.2);
  border-radius: var(--radius-xl);
  padding: var(--space-8) var(--space-10);
  transition: border-color var(--transition-normal), transform var(--transition-normal);
}

.logo-cta-card:hover {
  border-color: rgba(91, 143, 255, 0.4);
  transform: translateY(-2px);
}

.logo-cta-mark {
  flex-shrink: 0;
  opacity: 0.9;
}

.logo-cta-text {
  flex: 1;
}

.logo-cta-text h3 {
  font-family: var(--font-display);
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: var(--space-2);
}

.logo-cta-text p {
  font-size: 0.88rem;
  color: var(--text-muted);
  line-height: 1.6;
  margin: 0;
}

.logo-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--primary);
  background: var(--primary-dim);
  border: 1px solid rgba(91, 143, 255, 0.3);
  border-radius: var(--radius-full);
  padding: 10px 20px;
  transition: all var(--transition-fast);
}

.logo-cta-btn:hover {
  background: rgba(91, 143, 255, 0.2);
  border-color: var(--primary);
  transform: translateX(3px);
}

@media (max-width: 768px) {
  .logo-cta-card {
    flex-direction: column;
    text-align: center;
    gap: var(--space-5);
    padding: var(--space-8);
  }

  .logo-cta-btn {
    align-self: center;
  }
}
</style>
